// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { TimeSlot } from './TimeSlot';
import { Geometry } from './Geometry';
import { StructuredAddress } from './StructuredAddress';
import { LineItem } from './LineItem';
import { Skills } from './Skills';
import { AmountTransaction } from './AmountTransaction';
import { RouteRestriction } from './RouteRestriction';
import { ResourceDefinition } from './ResourceDefinition';
import { ContactPoint } from './ContactPoint';
import { VisitResource } from './VisitResource';
import { VehicleCostModel } from './VehicleCostModel';
import { TourFilter } from './TourFilter';
import { Amount } from './Amount';
import { ShiftInstant } from './ShiftInstant';
import { ResourceLimit } from './ResourceLimit';
import { VolumeLimit } from './VolumeLimit';
import { VersionedMapId } from './VersionedMapId';
import { TourDay } from './TourDay';
import { UnassignedDetail } from './UnassignedDetail';
import { GeocodingMetadata } from './GeocodingMetadata';
import { ZoneMapGeometry } from './ZoneMapGeometry';
import { Team } from './Team';
import { TriggeredGeofence } from './TriggeredGeofence';

import type { ContactPointModelType } from 'common/models/flow/ContactPoint';
import type { TimeSlotModelType } from './TimeSlot';
import type { GeometryModelType } from './Geometry';
import type { StructuredAddressModelType } from './StructuredAddress';
import type { LineItemModelType } from './LineItem';
import type { SkillsModelType } from './Skills';
import type { AmountTransactionModelType } from './AmountTransaction';
import type { RouteRestrictionModelType } from './RouteRestriction';
import type { ResourceDefinitionModelType } from './ResourceDefinition';
import type { VisitResourceModelType } from './VisitResource';
import type { VehicleCostModelModelType } from './VehicleCostModel';
import type { TourFilterModelType } from './TourFilter';
import type { AmountModelType } from './Amount';
import type { ShiftInstantModelType } from './ShiftInstant';
import type { ResourceLimitModelType } from './ResourceLimit';
import type { VolumeLimitModelType, VolumeLimitUnitEnum } from './VolumeLimit';
import type { VersionedMapIdModelType } from './VersionedMapId';
import type { TourDayModelType } from './TourDay';
import type { UnassignedDetailModelType } from './UnassignedDetail';
import type { TourDayReasonModelType } from './TourDayReason';
import type { MasterLocationModelType } from './MasterLocation';
import type { LocationIdModelType } from './LocationId';
import type { TaskStatusUpdateModelType } from './StatusUpdate';
import type { TriggeredGeofenceModelType } from './TriggeredGeofence';
import type { GeocodingMetadataModelType } from './GeocodingMetadata';
import type { CrossDockVehicleConfigModelType } from './CrossDockVehicleConfig';
import type { ZoneMapGeometryModelType } from './ZoneMapGeometry';
import type { TeamModelType } from './Team';

export type StatusEnum =
    | 'RECEIVED'
    | 'PLANNING'
    | 'PLANNED'
    | 'ASSIGNING'
    | 'ASSIGNED'
    | 'ERROR';
export type ProgressTypeEnum = 'PERCENT';
export type UnitEnum = 'COUNT' | 'SECONDS' | 'METERS' | 'KG' | 'UNIT';
export type ConstraintViolationTypeEnum = 'VOLUME' | 'TARDINESS';
export type ConstraintViolationSeverityEnum = 'LOW' | 'MEDIUM' | 'HIGH';
export type UserSourceEnum = 'LOCUS' | 'NONE';
export type BreakTypeEnum = 'LUNCH' | 'REFUEL';
export type TaskStatusEnum = 'WAITING' | 'ONGOING' | 'COMPLETED' | 'CANCELLED';
export type VisitStatusEnum =
    | 'WAITING'
    | 'QUEUED'
    | 'TRAVELLING'
    | 'TRANSACTING'
    | 'COMPLETED'
    | 'CANCELLED';
export type VisitSourceEnum = 'TASK' | 'USER';
export type VisitTypeEnum =
    | 'HOMEBASE'
    | 'CUSTOMER'
    | 'PICKUP'
    | 'DROP'
    | 'USER';
export type UserVisitTypeEnum =
    | 'SHIFT_START'
    | 'SHIFT_END'
    | 'TOUR_END'
    | 'BREAK'
    | 'OTHER'
    | 'NONE';
export type GenericBreakTypeEnum = 'LUNCH' | 'REFUEL' | 'NIGHT' | 'OTHER';
export type TransactionTypeEnum = 'PICKUP' | 'DROP';
export type AppFieldItemTypeEnum = 'URL' | 'TEXT';

export type PlanModelType = {
    vehicleGroups: Array<VehicleGroupModelType>,
    configuration: PlanConfigModelType,
    serializedVaeConfiguration: string,
    clientId: string,
    batchId: string,
    planId: string,
    isIncremental: boolean,
    replanIds: Array<string>,
    createdOn: string,
    updatedOn: string,
    progress: Array<ProgressModelType>,
    tours: Array<VehicleTourModelType>,
    summary: PlanSummaryModelType,
    status: StatusEnum,
    unassignedDetails: Array<UnassignedDetailModelType>,
    unassignedTasks: Array<UnassignedTaskModelType>,
    routeRestrictions: Array<RouteRestrictionModelType>,
    nextStatus: ?string,
    hasBeenFrozen: boolean,
    enableFrozenStatus: boolean,
    enableVehicleModelReplacement: boolean,
    allowOverlappingToursToSameUser: boolean,
    customExport: ?boolean,
    showTaskPriority: boolean,
    limitPlanEdits: boolean,
    tourFilters: Array<TourFilterModelType>,
    mapId: ?VersionedMapIdModelType,
    isMinimalPlan: boolean,
    isRosterPlan: boolean,
    metadataOnMinimalPlan: boolean,
    version: ?number,
    sequence?: number,
    originalPlanId: ?string
};

export type VehicleGroupModelType = {
    id: string,
    name: string,
    vehicles: Array<VehicleModelType>
};

export type PlanConfigModelType = {
    clusteringMode: string,
    fairnessMode: string,
    staticRouting: string,
    forceUseAllVehicles: string
};

export type ProgressModelType = {
    id: string,
    name: string,
    value: number,
    type: ProgressTypeEnum
};

export type VehicleTourModelType = {
    visits: Array<TourVisitModelType>,
    tourDaysInfo: Array<TourDayReasonModelType>,
    summary: UserTourSummaryModelType,
    tourId: TourIdModelType,
    sourceTourId: TourIdModelType,
    tourName: string,
    status?: string,
    isEdited?: boolean,
    tourDay?: TourDayModelType,
    tags: Array<string>,
    vehicle: UserVehicleModelType,
    assignedUser: ?AssignedUserModelType,
    customProperties: { [key: string]: string }
};

export type AdditionalValueModelType = {
    url: string
};

export type AppFieldItemModelType = {
    item: string,
    format: AppFieldItemTypeEnum,
    additionalValues: ?AdditionalValueModelType
};

export type AppFieldsModelType = {
    items: Array<AppFieldItemModelType>
};

export type VehicleCountModelType = {
    name: string,
    count: number
};

export type CarbonEmissionModelType = {
    equivalentTreeOffset: number,
    savedCarbonEmission: number,
    plannedCarbonEmission: number
};

export type PlanSummaryModelType = {
    totalTasks: number,
    assignedTasks: number,
    totalTimeOnRoad: number,
    totalTardiness: number,
    totalIdleTime: number,
    totalTravelDistance: number,
    totalTransactionDuration: number,
    totalVehicles: number,
    vehiclesUsed: number,
    totalUsedVehiclesVolume: number,
    vehiclesOperationSlot: TimeSlotModelType,
    editsCount: number,
    editsSummary: ?PlanEditsSummaryModelType,
    volume: ResourceUtilizationModelType,
    time: ResourceUtilizationModelType,
    resources: ResourcesModelType,
    customMetrics: { [key: string]: string },
    totalFreightCost: ?AmountModelType,
    tourCount?: number,
    vehicleModelsCount: { [string]: VehicleCountModelType },
    vehicleModelsUsed: { [string]: VehicleCountModelType },
    carbonEmissionMetrics: ?CarbonEmissionModelType,
    planVolumeUnit: ?VolumeLimitUnitEnum,
    violationTypes: Array<string>,
    totalVisitsWithBreach: number
};

export type PlanEditsSummaryModelType = {
    allowedTaskMoves: ?number,
    editCount: number,
    editCounts: Object,
    incrementalEditCounts: Object,
    netTasksAllocated: number,
    netTasksUnallocated: number,
    netTasksMoved: number,
    netToursAltered: number,
    vehiclesAdded: number,
    vehiclesChanged: number,
    vehiclesDeleted: number
};

export type ResourcesModelType = {
    [key: string]: ResourceUtilizationModelType
};

export type MetricModelType = {
    aggregation: string,
    value: number
};

export type CustomFieldMetricModelType = {
    key: string,
    metric: MetricModelType
};

export type UnassignedTaskModelType = {
    clientId: string,
    taskId: string,
    userId: ?string,
    visits: Array<TourVisitModelType>,
    skills: SkillsModelType,
    possibleDays: Array<TourDayModelType>
};

export type VehicleModelType = {
    id: VehicleIdModelType,
    vehicleModel: VehicleModelModelType,
    operationShift: OperationShiftModelType,
    resourceLimits: Array<ResourceLimitModelType>
};

export type TourVisitModelType = {
    id: string,
    sourceId: string,
    clientId: string,
    allocatedPlanId: string,
    taskStatus: TaskStatusEnum,
    taskPriority: ?number,
    visitStatus: VisitStatusEnum,
    chosenLocation: VisitLocationModelType,
    currentEta: string,
    currentEtd: string,
    initialEta: string,
    initialEtd: string,
    initialEtt: string,
    idleDuration: number,
    currentEtt: string,
    slaBreached: boolean,
    predictedTravel: TimeDistancePairModelType,
    volume: VisitVolumeModelType,
    volumeFilled: number,
    resources: Array<VisitResourceModelType>,
    visitSource: VisitSourceEnum,
    visitType: VisitTypeEnum,
    transactionType: TransactionTypeEnum,
    justModified: boolean,
    violations: Array<ConstraintViolationModelType>,
    orderDetail: OrderDetailModelType,
    amountTransaction: AmountTransactionModelType,
    sourceOrderId: string,
    userVisitType: UserVisitTypeEnum,
    breakType: GenericBreakTypeEnum,
    appFields: AppFieldsModelType,
    skills: SkillsModelType,
    possibleDays: Array<TourDayModelType>,
    predictedPolyline: ?string,
    locationId: ?LocationIdModelType,
    locationDetails: ?MasterLocationModelType,
    tardiness: ?number,
    statusUpdates: TaskStatusUpdateModelType,
    triggeredGeofences: ?Array<TriggeredGeofenceModelType>,
    routeId: string,
    routes: Array<ZoneMapGeometryModelType>,
    team: TeamModelType,
    returnTask: ?boolean,
    category: ?string,
    compartmentId: ?string
};

export type UsedRoutesModelType = {
    mapId: VersionedMapIdModelType,
    routeId: string
};

export type UserTourSummaryModelType = {
    clientId: string,
    startTime: string,
    customMetrics: { [key: string]: string },
    endTime: string,
    taskCount: number,
    visitCount: number,
    totalTravelDistance: number,
    totalTravelDuration: number,
    totalTardiness: number,
    totalIdleDuration: number,
    totalTransactionDuration: number,
    totalDuration?: number,
    volume: ResourceUtilizationModelType,
    resources: ?ResourcesModelType,
    vehiclesOperationSlot: TimeSlotModelType,
    usedSkills: SkillsModelType,
    freightCost: ?AmountModelType,
    usedRoutes: ?Array<UsedRoutesModelType>,
    customFieldMetrics: Array<CustomFieldMetricModelType>,
    tourCarbonEmission: ?number
};

export type TourIdModelType = {
    clientId: string,
    tourId: string
};

export type UserVehicleModelType = {
    idAttr: string,
    id: VehicleIdModelType,
    vehicleModel: VehicleModelModelType,
    operationShift: OperationShiftModelType,
    resourceLimits: Array<ResourceLimitModelType>,
    userAlias: ?UserAliasModelType,
    skills: SkillsModelType,
    rating: number,
    vehicleCostModel: ?VehicleCostModelModelType,
    isCrossDockEnabled: ?boolean,
    crossDockVehicleConfig: ?CrossDockVehicleConfigModelType
};

export type AssignedUserModelType = {
    carrierClientId: string,
    userId: string,
    name: string
};

export type ResourceUtilizationModelType = {
    used: number,
    total: number,
    available: number,
    required: number,
    definition: ?ResourceDefinitionModelType
};

export type VehicleIdModelType = {
    clientId: string,
    id: string,
    version?: ?number
};

export type VehicleModelModelType = {
    name: string,
    idAttr: string,
    volumeLimit: VolumeLimitModelType,
    id: VehicleModelIdModelType,
    costModel: ?VehicleCostModelModelType,
    skills: ?SkillsModelType,
    resourceLimits: Array<ResourceLimitModelType>
};

export type OperationShiftModelType = {
    startTime: string,
    startShiftInstant: ShiftInstantModelType,
    startLocation: ?MinimalLocationModelType,
    endTime: string,
    endShiftInstant: ShiftInstantModelType,
    endLocation: ?MinimalLocationModelType,
    breaks: Array<BreakModelType>
};

export type VisitLocationModelType = {
    id: string,
    slot: VisitSlotModelType,
    contact: ?ContactPointModelType,
    address: StructuredAddressModelType,
    geometry: GeometryModelType,
    geocodingMetadata: GeocodingMetadataModelType,
    nonAvailableSlots: Array<VisitSlotModelType>
};

export type TimeDistancePairModelType = {
    distance: number,
    duration: number
};

export type VisitVolumeModelType = {
    value: string,
    exchangeType: string
};

export type ConstraintViolationModelType = {
    type: ConstraintViolationTypeEnum,
    breachValue: string,
    threshold: string,
    unit: string,
    severity: ConstraintViolationSeverityEnum,
    description: string,
    name: string
};

export type OrderDetailModelType = {
    lineItems: ?Array<LineItemModelType>
};

export type UserAliasModelType = {
    alias: string,
    clientId: string,
    source: UserSourceEnum
};

export type VehicleModelIdModelType = {
    clientId: string,
    modelId: string
};

export type MinimalLocationModelType = {
    lat: number,
    lng: number,
    description: string
};

export type BreakModelType = {
    startTime: string,
    startShiftInstant: ShiftInstantModelType,
    startLocation: ?MinimalLocationModelType,
    endTime: string,
    endShiftInstant: ShiftInstantModelType,
    endLocation: ?MinimalLocationModelType,
    transactionDuration: number,
    breakType: BreakTypeEnum
};

export type VisitSlotModelType = {
    startTime: string,
    endTime: string,
    transactionDuration: number
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Plan extends ImmutableModel {
    static fromJS(json: PlanModelType): Plan {
        const state: Object = Object.assign({}, json);
        state.vehicleGroups = Immutable.List(state.vehicleGroups).map(item =>
            VehicleGroup.fromJS(item)
        );
        state.configuration = PlanConfig.fromJS(state.configuration);
        state.replanIds = Immutable.List(state.replanIds);
        state.progress = Immutable.List(state.progress).map(item =>
            Progress.fromJS(item)
        );
        state.tours = Immutable.List(state.tours).map(item =>
            VehicleTour.fromJS(item)
        );
        state.summary = PlanSummary.fromJS(state.summary);
        state.unassignedDetails = Immutable.List(
            state.unassignedDetails
        ).map(item => UnassignedDetail.fromJS(item));
        state.unassignedTasks = Immutable.List(
            state.unassignedTasks
        ).map(item => UnassignedTask.fromJS(item));
        state.routeRestrictions = Immutable.List(
            state.routeRestrictions
        ).map(item => RouteRestriction.fromJS(item));
        state.tourFilters = Immutable.List(state.tourFilters).map(item =>
            TourFilter.fromJS(item)
        );
        state.mapId = state.mapId
            ? VersionedMapId.fromJS(state.mapId)
            : state.mapId;
        return new this(Immutable.Map(state));
    }

    toJS(): PlanModelType {
        const js: PlanModelType = {
            vehicleGroups: this.vehicleGroups
                .toArray()
                .map(item => item.toJS()),
            configuration: this.configuration.toJS(),
            serializedVaeConfiguration: this.serializedVaeConfiguration,
            clientId: this.clientId,
            batchId: this.batchId,
            planId: this.planId,
            isIncremental: this.isIncremental,
            replanIds: this.replanIds.toArray(),
            createdOn: this.createdOn,
            updatedOn: this.updatedOn,
            progress: this.progress.toArray().map(item => item.toJS()),
            tours: this.tours.toArray().map(item => item.toJS()),
            summary: this.summary.toJS(),
            status: this.status,
            unassignedDetails: this.unassignedDetails
                .toArray()
                .map(item => item.toJS()),
            unassignedTasks: this.unassignedTasks
                .toArray()
                .map(item => item.toJS()),
            routeRestrictions: this.routeRestrictions
                .toArray()
                .map(item => item.toJS()),
            nextStatus: this.nextStatus,
            hasBeenFrozen: this.hasBeenFrozen,
            enableFrozenStatus: this.enableFrozenStatus,
            enableVehicleModelReplacement: this.enableVehicleModelReplacement,
            allowOverlappingToursToSameUser: this
                .allowOverlappingToursToSameUser,
            customExport: this.customExport,
            showTaskPriority: this.showTaskPriority,
            limitPlanEdits: this.limitPlanEdits,
            tourFilters: this.tourFilters.toArray().map(item => item.toJS()),
            mapId: this.mapId ? this.mapId.toJS() : this.mapId,
            isMinimalPlan: this.isMinimalPlan,
            isRosterPlan: this.isRosterPlan,
            metadataOnMinimalPlan: this.metadataOnMinimalPlan,
            version: this.version,
            originalPlanId: this.originalPlanId
        };

        if (this.sequence != null) {
            js.sequence = this.sequence;
        }

        return js;
    }

    get vehicleGroups(): Immutable.List<VehicleGroup> {
        return this._state.get('vehicleGroups');
    }

    setVehicleGroups(vehicleGroups: Immutable.List<VehicleGroup>): this {
        return this.clone(this._state.set('vehicleGroups', vehicleGroups));
    }

    get configuration(): PlanConfig {
        return this._state.get('configuration');
    }

    setConfiguration(configuration: PlanConfig): this {
        return this.clone(this._state.set('configuration', configuration));
    }

    get serializedVaeConfiguration(): string {
        return this._state.get('serializedVaeConfiguration');
    }

    setSerializedVaeConfiguration(serializedVaeConfiguration: string): this {
        return this.clone(
            this._state.set(
                'serializedVaeConfiguration',
                serializedVaeConfiguration
            )
        );
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get batchId(): string {
        return this._state.get('batchId');
    }

    setBatchId(batchId: string): this {
        return this.clone(this._state.set('batchId', batchId));
    }

    get planId(): string {
        return this._state.get('planId');
    }

    setPlanId(planId: string): this {
        return this.clone(this._state.set('planId', planId));
    }

    get isIncremental(): boolean {
        return this._state.get('isIncremental');
    }

    setIsIncremental(isIncremental: boolean): this {
        return this.clone(this._state.set('isIncremental', isIncremental));
    }

    get replanIds(): Immutable.List<string> {
        return this._state.get('replanIds');
    }

    setReplanIds(replanIds: Immutable.List<string>): this {
        return this.clone(this._state.set('replanIds', replanIds));
    }

    get createdOn(): string {
        return this._state.get('createdOn');
    }

    setCreatedOn(createdOn: string): this {
        return this.clone(this._state.set('createdOn', createdOn));
    }

    get updatedOn(): string {
        return this._state.get('updatedOn');
    }

    setUpdatedOn(updatedOn: string): this {
        return this.clone(this._state.set('updatedOn', updatedOn));
    }

    get progress(): Immutable.List<Progress> {
        return this._state.get('progress');
    }

    setProgress(progress: Immutable.List<Progress>): this {
        return this.clone(this._state.set('progress', progress));
    }

    get tours(): Immutable.List<VehicleTour> {
        return this._state.get('tours');
    }

    setTours(tours: Immutable.List<VehicleTour>): this {
        return this.clone(this._state.set('tours', tours));
    }

    get summary(): PlanSummary {
        return this._state.get('summary');
    }

    setSummary(summary: PlanSummary): this {
        return this.clone(this._state.set('summary', summary));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get unassignedDetails(): Immutable.List<UnassignedDetail> {
        return this._state.get('unassignedDetails');
    }

    setUnassignedDetails(
        unassignedDetails: Immutable.List<UnassignedDetail>
    ): this {
        return this.clone(
            this._state.set('unassignedDetails', unassignedDetails)
        );
    }

    get unassignedTasks(): Immutable.List<UnassignedTask> {
        return this._state.get('unassignedTasks');
    }

    setUnassignedTasks(unassignedTasks: Immutable.List<UnassignedTask>): this {
        return this.clone(this._state.set('unassignedTasks', unassignedTasks));
    }

    get routeRestrictions(): Immutable.List<RouteRestriction> {
        return this._state.get('routeRestrictions');
    }

    setRouteRestrictions(
        routeRestrictions: Immutable.List<RouteRestriction>
    ): this {
        return this.clone(
            this._state.set('routeRestrictions', routeRestrictions)
        );
    }

    get nextStatus(): ?string {
        return this._state.get('nextStatus');
    }

    setNextStatus(nextStatus: ?string): this {
        return this.clone(this._state.set('nextStatus', nextStatus));
    }

    get hasBeenFrozen(): boolean {
        return this._state.get('hasBeenFrozen');
    }

    setHasBeenFrozen(hasBeenFrozen: boolean): this {
        return this.clone(this._state.set('hasBeenFrozen', hasBeenFrozen));
    }

    get enableFrozenStatus(): boolean {
        return this._state.get('enableFrozenStatus');
    }

    setEnableFrozenStatus(enableFrozenStatus: boolean): this {
        return this.clone(
            this._state.set('enableFrozenStatus', enableFrozenStatus)
        );
    }

    get enableVehicleModelReplacement(): boolean {
        return this._state.get('enableVehicleModelReplacement');
    }

    setEnableVehicleModelReplacement(
        enableVehicleModelReplacement: boolean
    ): this {
        return this.clone(
            this._state.set(
                'enableVehicleModelReplacement',
                enableVehicleModelReplacement
            )
        );
    }

    get allowOverlappingToursToSameUser(): boolean {
        return this._state.get('allowOverlappingToursToSameUser');
    }

    setAllowOverlappingToursToSameUser(
        allowOverlappingToursToSameUser: boolean
    ): this {
        return this.clone(
            this._state.set(
                'allowOverlappingToursToSameUser',
                allowOverlappingToursToSameUser
            )
        );
    }

    get customExport(): ?boolean {
        return this._state.get('customExport');
    }

    setCustomExport(customExport: ?boolean): this {
        return this.clone(this._state.set('customExport', customExport));
    }

    get showTaskPriority(): boolean {
        return this._state.get('showTaskPriority');
    }

    setShowTaskPriority(showTaskPriority: boolean): this {
        return this.clone(
            this._state.set('showTaskPriority', showTaskPriority)
        );
    }

    get limitPlanEdits(): boolean {
        return this._state.get('limitPlanEdits');
    }

    setLimitPlanEdits(limitPlanEdits: boolean): this {
        return this.clone(this._state.set('limitPlanEdits', limitPlanEdits));
    }

    get tourFilters(): Immutable.List<TourFilter> {
        return this._state.get('tourFilters');
    }

    setTourFilters(tourFilters: Immutable.List<TourFilter>): this {
        return this.clone(this._state.set('tourFilters', tourFilters));
    }

    get mapId(): ?VersionedMapId {
        return this._state.get('mapId');
    }

    setMapId(mapId: ?VersionedMapId): this {
        return this.clone(this._state.set('mapId', mapId));
    }

    get isMinimalPlan(): boolean {
        return this._state.get('isMinimalPlan');
    }

    setIsMinimalPlan(isMinimalPlan: boolean): this {
        return this.clone(this._state.set('isMinimalPlan', isMinimalPlan));
    }

    get isRosterPlan(): boolean {
        return this._state.get('isRosterPlan');
    }

    setIsRosterPlan(isRosterPlan: boolean): this {
        return this.clone(this._state.set('isRosterPlan', isRosterPlan));
    }

    get metadataOnMinimalPlan(): boolean {
        return this._state.get('metadataOnMinimalPlan');
    }

    setMetadataOnMinimalPlan(metadataOnMinimalPlan: boolean): this {
        return this.clone(
            this._state.set('metadataOnMinimalPlan', metadataOnMinimalPlan)
        );
    }

    get version(): ?number {
        return this._state.get('version');
    }

    setVersion(version: ?number): this {
        return this.clone(this._state.set('version', version));
    }

    get sequence(): ?number {
        return this._state.get('sequence');
    }

    setSequence(sequence: ?number): this {
        return this.clone(this._state.set('sequence', sequence));
    }

    get originalPlanId(): ?string {
        return this._state.get('originalPlanId');
    }

    setOriginalPlanId(originalPlanId: ?string): this {
        return this.clone(this._state.set('originalPlanId', originalPlanId));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VehicleGroup extends ImmutableModel {
    static fromJS(json: VehicleGroupModelType): VehicleGroup {
        const state: Object = Object.assign({}, json);
        state.vehicles = Immutable.List(state.vehicles).map(item =>
            Vehicle.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): VehicleGroupModelType {
        return {
            id: this.id,
            name: this.name,
            vehicles: this.vehicles.toArray().map(item => item.toJS())
        };
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get vehicles(): Immutable.List<Vehicle> {
        return this._state.get('vehicles');
    }

    setVehicles(vehicles: Immutable.List<Vehicle>): this {
        return this.clone(this._state.set('vehicles', vehicles));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class PlanConfig extends ImmutableModel {
    static fromJS(json: PlanConfigModelType): PlanConfig {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): PlanConfigModelType {
        return {
            clusteringMode: this.clusteringMode,
            fairnessMode: this.fairnessMode,
            staticRouting: this.staticRouting,
            forceUseAllVehicles: this.forceUseAllVehicles
        };
    }

    get clusteringMode(): string {
        return this._state.get('clusteringMode');
    }

    setClusteringMode(clusteringMode: string): this {
        return this.clone(this._state.set('clusteringMode', clusteringMode));
    }

    get fairnessMode(): string {
        return this._state.get('fairnessMode');
    }

    setFairnessMode(fairnessMode: string): this {
        return this.clone(this._state.set('fairnessMode', fairnessMode));
    }

    get staticRouting(): string {
        return this._state.get('staticRouting');
    }

    setStaticRouting(staticRouting: string): this {
        return this.clone(this._state.set('staticRouting', staticRouting));
    }

    get forceUseAllVehicles(): string {
        return this._state.get('forceUseAllVehicles');
    }

    setForceUseAllVehicles(forceUseAllVehicles: string): this {
        return this.clone(
            this._state.set('forceUseAllVehicles', forceUseAllVehicles)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Progress extends ImmutableModel {
    static fromJS(json: ProgressModelType): Progress {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): ProgressModelType {
        return {
            id: this.id,
            name: this.name,
            value: this.value,
            type: this.type
        };
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get value(): number {
        return this._state.get('value');
    }

    setValue(value: number): this {
        return this.clone(this._state.set('value', value));
    }

    get type(): ProgressTypeEnum {
        return this._state.get('type');
    }

    setType(type: ProgressTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VehicleTour extends ImmutableModel {
    static fromJS(json: VehicleTourModelType): VehicleTour {
        const state: Object = Object.assign({}, json);
        state.visits = Immutable.List(state.visits).map(item =>
            TourVisit.fromJS(item)
        );
        state.tourDaysInfo = Immutable.List(state.tourDaysInfo).map(item =>
            TourDayReason.fromJS(item)
        );
        state.summary = UserTourSummary.fromJS(state.summary);
        state.tourId = TourId.fromJS(state.tourId);
        state.sourceTourId = TourId.fromJS(state.sourceTourId);
        state.tourDay = state.tourDay
            ? TourDay.fromJS(state.tourDay)
            : state.tourDay;
        state.tags = Immutable.List(state.tags);
        state.vehicle = UserVehicle.fromJS(state.vehicle);
        state.assignedUser = state.assignedUser
            ? AssignedUser.fromJS(state.assignedUser)
            : state.assignedUser;
        state.customProperties = Immutable.Map(state.customProperties);
        return new this(Immutable.Map(state));
    }

    toJS(): VehicleTourModelType {
        const js: VehicleTourModelType = {
            visits: this.visits.toArray().map(item => item.toJS()),
            tourDaysInfo: this.tourDaysInfo.toArray().map(item => item.toJS()),
            summary: this.summary.toJS(),
            tourId: this.tourId.toJS(),
            sourceTourId: this.sourceTourId.toJS(),
            tourName: this.tourName,
            tags: this.tags.toArray(),
            vehicle: this.vehicle.toJS(),
            assignedUser: this.assignedUser
                ? this.assignedUser.toJS()
                : this.assignedUser,
            customProperties: this.customProperties.toObject()
        };

        if (this.status != null) {
            js.status = this.status;
        }

        if (this.isEdited != null) {
            js.isEdited = this.isEdited;
        }

        if (this.tourDay != null) {
            js.tourDay = this.tourDay ? this.tourDay.toJS() : this.tourDay;
        }

        return js;
    }

    get visits(): Immutable.List<TourVisit> {
        return this._state.get('visits');
    }

    setVisits(visits: Immutable.List<TourVisit>): this {
        return this.clone(this._state.set('visits', visits));
    }

    get tourDaysInfo(): Immutable.List<TourDayReason> {
        return this._state.get('tourDaysInfo');
    }

    setTourDaysInfo(tourDaysInfo: Immutable.List<TourDayReason>): this {
        return this.clone(this._state.set('tourDaysInfo', tourDaysInfo));
    }

    get summary(): UserTourSummary {
        return this._state.get('summary');
    }

    setSummary(summary: UserTourSummary): this {
        return this.clone(this._state.set('summary', summary));
    }

    get tourId(): TourId {
        return this._state.get('tourId');
    }

    setTourId(tourId: TourId): this {
        return this.clone(this._state.set('tourId', tourId));
    }

    get sourceTourId(): TourId {
        return this._state.get('sourceTourId');
    }

    setSourceTourId(sourceTourId: TourId): this {
        return this.clone(this._state.set('sourceTourId', sourceTourId));
    }

    get tourName(): string {
        return this._state.get('tourName');
    }

    setTourName(tourName: string): this {
        return this.clone(this._state.set('tourName', tourName));
    }

    get status(): ?string {
        return this._state.get('status');
    }

    setStatus(status: ?string): this {
        return this.clone(this._state.set('status', status));
    }

    get isEdited(): ?boolean {
        return this._state.get('isEdited');
    }

    setIsEdited(isEdited: ?boolean): this {
        return this.clone(this._state.set('isEdited', isEdited));
    }

    get tourDay(): ?TourDay {
        return this._state.get('tourDay');
    }

    setTourDay(tourDay: ?TourDay): this {
        return this.clone(this._state.set('tourDay', tourDay));
    }

    get tags(): Immutable.List<string> {
        return this._state.get('tags');
    }

    setTags(tags: Immutable.List<string>): this {
        return this.clone(this._state.set('tags', tags));
    }

    get vehicle(): UserVehicle {
        return this._state.get('vehicle');
    }

    setVehicle(vehicle: UserVehicle): this {
        return this.clone(this._state.set('vehicle', vehicle));
    }

    get assignedUser(): ?AssignedUser {
        return this._state.get('assignedUser');
    }

    setAssignedUser(assignedUser: ?AssignedUser): this {
        return this.clone(this._state.set('assignedUser', assignedUser));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AppFieldItem extends ImmutableModel {
    static fromJS(json: AppFieldItemModelType): AppFieldItem {
        const state: Object = Object.assign({}, json);
        state.additionalValues = state.additionalValues
            ? AdditionalValue.fromJS(state.additionalValues)
            : state.additionalValues;
        return new this(Immutable.Map(state));
    }

    toJS(): AppFieldItemModelType {
        return {
            item: this.item,
            format: this.format,
            additionalValues: this.additionalValues
                ? this.additionalValues.toJS()
                : this.additionalValues
        };
    }

    get item(): string {
        return this._state.get('item');
    }

    setItem(item: string): this {
        return this.clone(this._state.set('item', item));
    }

    get format(): AppFieldItemTypeEnum {
        return this._state.get('format');
    }

    setFormat(format: AppFieldItemTypeEnum): this {
        return this.clone(this._state.set('format', format));
    }

    get additionalValues(): ?AdditionalValue {
        return this._state.get('additionalValues');
    }

    setAdditionalValues(additionalValues: ?AdditionalValue): this {
        return this.clone(
            this._state.set('additionalValues', additionalValues)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AppFields extends ImmutableModel {
    static fromJS(json: AppFieldsModelType): AppFields {
        const state: Object = Object.assign({}, json);
        state.items = Immutable.List(state.items).map(item =>
            AppFieldItem.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): AppFieldsModelType {
        return {
            items: this.items.toArray().map(item => item.toJS())
        };
    }

    get items(): Immutable.List<AppFieldItem> {
        return this._state.get('items');
    }

    setItems(items: Immutable.List<AppFieldItem>): this {
        return this.clone(this._state.set('items', items));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class PlanSummary extends ImmutableModel {
    static fromJS(json: PlanSummaryModelType): PlanSummary {
        const state: Object = Object.assign({}, json);
        state.vehiclesOperationSlot = TimeSlot.fromJS(
            state.vehiclesOperationSlot
        );
        state.editsSummary = state.editsSummary
            ? PlanEditsSummary.fromJS(state.editsSummary)
            : state.editsSummary;
        state.volume = ResourceUtilization.fromJS(state.volume);
        state.time = ResourceUtilization.fromJS(state.time);
        state.resources = Resources.fromJS(state.resources);
        state.customMetrics = Immutable.Map(state.customMetrics);
        state.totalFreightCost = state.totalFreightCost
            ? Amount.fromJS(state.totalFreightCost)
            : state.totalFreightCost;
        state.vehicleModelsCount = Immutable.Map(
            state.vehicleModelsCount
        ).map(item => VehicleCount.fromJS(item));
        state.vehicleModelsUsed = Immutable.Map(
            state.vehicleModelsUsed
        ).map(item => VehicleCount.fromJS(item));
        state.carbonEmissionMetrics = state.carbonEmissionMetrics
            ? CarbonEmission.fromJS(state.carbonEmissionMetrics)
            : state.carbonEmissionMetrics;
        state.violationTypes = Immutable.List(state.violationTypes);
        return new this(Immutable.Map(state));
    }

    toJS(): PlanSummaryModelType {
        const js: PlanSummaryModelType = {
            totalTasks: this.totalTasks,
            assignedTasks: this.assignedTasks,
            totalTimeOnRoad: this.totalTimeOnRoad,
            totalTardiness: this.totalTardiness,
            totalIdleTime: this.totalIdleTime,
            totalTravelDistance: this.totalTravelDistance,
            totalTransactionDuration: this.totalTransactionDuration,
            totalVehicles: this.totalVehicles,
            vehiclesUsed: this.vehiclesUsed,
            totalUsedVehiclesVolume: this.totalUsedVehiclesVolume,
            vehiclesOperationSlot: this.vehiclesOperationSlot.toJS(),
            editsCount: this.editsCount,
            editsSummary: this.editsSummary
                ? this.editsSummary.toJS()
                : this.editsSummary,
            volume: this.volume.toJS(),
            time: this.time.toJS(),
            resources: this.resources.toJS(),
            customMetrics: this.customMetrics.toObject(),
            totalFreightCost: this.totalFreightCost
                ? this.totalFreightCost.toJS()
                : this.totalFreightCost,
            vehicleModelsCount: this.vehicleModelsCount
                .map(item => item.toJS())
                .toObject(),
            vehicleModelsUsed: this.vehicleModelsUsed
                .map(item => item.toJS())
                .toObject(),
            carbonEmissionMetrics: this.carbonEmissionMetrics
                ? this.carbonEmissionMetrics.toJS()
                : this.carbonEmissionMetrics,
            planVolumeUnit: this.planVolumeUnit,
            violationTypes: this.violationTypes.toArray(),
            totalVisitsWithBreach: this.totalVisitsWithBreach
        };

        if (this.tourCount != null) {
            js.tourCount = this.tourCount;
        }

        return js;
    }

    get totalTasks(): number {
        return this._state.get('totalTasks');
    }

    setTotalTasks(totalTasks: number): this {
        return this.clone(this._state.set('totalTasks', totalTasks));
    }

    get assignedTasks(): number {
        return this._state.get('assignedTasks');
    }

    setAssignedTasks(assignedTasks: number): this {
        return this.clone(this._state.set('assignedTasks', assignedTasks));
    }

    get totalTimeOnRoad(): number {
        return this._state.get('totalTimeOnRoad');
    }

    setTotalTimeOnRoad(totalTimeOnRoad: number): this {
        return this.clone(this._state.set('totalTimeOnRoad', totalTimeOnRoad));
    }

    get totalTardiness(): number {
        return this._state.get('totalTardiness');
    }

    setTotalTardiness(totalTardiness: number): this {
        return this.clone(this._state.set('totalTardiness', totalTardiness));
    }

    get totalIdleTime(): number {
        return this._state.get('totalIdleTime');
    }

    setTotalIdleTime(totalIdleTime: number): this {
        return this.clone(this._state.set('totalIdleTime', totalIdleTime));
    }

    get totalTravelDistance(): number {
        return this._state.get('totalTravelDistance');
    }

    setTotalTravelDistance(totalTravelDistance: number): this {
        return this.clone(
            this._state.set('totalTravelDistance', totalTravelDistance)
        );
    }

    get totalTransactionDuration(): number {
        return this._state.get('totalTransactionDuration');
    }

    setTotalTransactionDuration(totalTransactionDuration: number): this {
        return this.clone(
            this._state.set(
                'totalTransactionDuration',
                totalTransactionDuration
            )
        );
    }

    get totalVehicles(): number {
        return this._state.get('totalVehicles');
    }

    setTotalVehicles(totalVehicles: number): this {
        return this.clone(this._state.set('totalVehicles', totalVehicles));
    }

    get vehiclesUsed(): number {
        return this._state.get('vehiclesUsed');
    }

    setVehiclesUsed(vehiclesUsed: number): this {
        return this.clone(this._state.set('vehiclesUsed', vehiclesUsed));
    }

    get totalUsedVehiclesVolume(): number {
        return this._state.get('totalUsedVehiclesVolume');
    }

    setTotalUsedVehiclesVolume(totalUsedVehiclesVolume: number): this {
        return this.clone(
            this._state.set('totalUsedVehiclesVolume', totalUsedVehiclesVolume)
        );
    }

    get vehiclesOperationSlot(): TimeSlot {
        return this._state.get('vehiclesOperationSlot');
    }

    setVehiclesOperationSlot(vehiclesOperationSlot: TimeSlot): this {
        return this.clone(
            this._state.set('vehiclesOperationSlot', vehiclesOperationSlot)
        );
    }

    get editsCount(): number {
        return this._state.get('editsCount');
    }

    setEditsCount(editsCount: number): this {
        return this.clone(this._state.set('editsCount', editsCount));
    }

    get editsSummary(): ?PlanEditsSummary {
        return this._state.get('editsSummary');
    }

    setEditsSummary(editsSummary: ?PlanEditsSummary): this {
        return this.clone(this._state.set('editsSummary', editsSummary));
    }

    get volume(): ResourceUtilization {
        return this._state.get('volume');
    }

    setVolume(volume: ResourceUtilization): this {
        return this.clone(this._state.set('volume', volume));
    }

    get time(): ResourceUtilization {
        return this._state.get('time');
    }

    setTime(time: ResourceUtilization): this {
        return this.clone(this._state.set('time', time));
    }

    get resources(): Resources {
        return this._state.get('resources');
    }

    setResources(resources: Resources): this {
        return this.clone(this._state.set('resources', resources));
    }

    get customMetrics(): Immutable.Map<string, string> {
        return this._state.get('customMetrics');
    }

    setCustomMetrics(customMetrics: Immutable.Map<string, string>): this {
        return this.clone(this._state.set('customMetrics', customMetrics));
    }

    get totalFreightCost(): ?Amount {
        return this._state.get('totalFreightCost');
    }

    setTotalFreightCost(totalFreightCost: ?Amount): this {
        return this.clone(
            this._state.set('totalFreightCost', totalFreightCost)
        );
    }

    get tourCount(): ?number {
        return this._state.get('tourCount');
    }

    setTourCount(tourCount: ?number): this {
        return this.clone(this._state.set('tourCount', tourCount));
    }

    get vehicleModelsCount(): Immutable.Map<string, VehicleCount> {
        return this._state.get('vehicleModelsCount');
    }

    setVehicleModelsCount(
        vehicleModelsCount: Immutable.Map<string, VehicleCount>
    ): this {
        return this.clone(
            this._state.set('vehicleModelsCount', vehicleModelsCount)
        );
    }

    get vehicleModelsUsed(): Immutable.Map<string, VehicleCount> {
        return this._state.get('vehicleModelsUsed');
    }

    setVehicleModelsUsed(
        vehicleModelsUsed: Immutable.Map<string, VehicleCount>
    ): this {
        return this.clone(
            this._state.set('vehicleModelsUsed', vehicleModelsUsed)
        );
    }

    get carbonEmissionMetrics(): ?CarbonEmission {
        return this._state.get('carbonEmissionMetrics');
    }

    setCarbonEmissionMetrics(carbonEmissionMetrics: ?CarbonEmission): this {
        return this.clone(
            this._state.set('carbonEmissionMetrics', carbonEmissionMetrics)
        );
    }

    get planVolumeUnit(): ?VolumeLimitUnitEnum {
        return this._state.get('planVolumeUnit');
    }

    setPlanVolumeUnit(planVolumeUnit: ?VolumeLimitUnitEnum): this {
        return this.clone(this._state.set('planVolumeUnit', planVolumeUnit));
    }

    get violationTypes(): Immutable.List<string> {
        return this._state.get('violationTypes');
    }

    setViolationTypes(violationTypes: Immutable.List<string>): this {
        return this.clone(this._state.set('violationTypes', violationTypes));
    }

    get totalVisitsWithBreach(): number {
        return this._state.get('totalVisitsWithBreach');
    }

    setTotalVisitsWithBreach(totalVisitsWithBreach: number): this {
        return this.clone(
            this._state.set('totalVisitsWithBreach', totalVisitsWithBreach)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class PlanEditsSummary extends ImmutableModel {
    static fromJS(json: PlanEditsSummaryModelType): PlanEditsSummary {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): PlanEditsSummaryModelType {
        return {
            allowedTaskMoves: this.allowedTaskMoves,
            editCount: this.editCount,
            editCounts: this.editCounts,
            incrementalEditCounts: this.incrementalEditCounts,
            netTasksAllocated: this.netTasksAllocated,
            netTasksUnallocated: this.netTasksUnallocated,
            netTasksMoved: this.netTasksMoved,
            netToursAltered: this.netToursAltered,
            vehiclesAdded: this.vehiclesAdded,
            vehiclesChanged: this.vehiclesChanged,
            vehiclesDeleted: this.vehiclesDeleted
        };
    }

    get allowedTaskMoves(): ?number {
        return this._state.get('allowedTaskMoves');
    }

    setAllowedTaskMoves(allowedTaskMoves: ?number): this {
        return this.clone(
            this._state.set('allowedTaskMoves', allowedTaskMoves)
        );
    }

    get editCount(): number {
        return this._state.get('editCount');
    }

    setEditCount(editCount: number): this {
        return this.clone(this._state.set('editCount', editCount));
    }

    get editCounts(): Object {
        return this._state.get('editCounts');
    }

    setEditCounts(editCounts: Object): this {
        return this.clone(this._state.set('editCounts', editCounts));
    }

    get incrementalEditCounts(): Object {
        return this._state.get('incrementalEditCounts');
    }

    setIncrementalEditCounts(incrementalEditCounts: Object): this {
        return this.clone(
            this._state.set('incrementalEditCounts', incrementalEditCounts)
        );
    }

    get netTasksAllocated(): number {
        return this._state.get('netTasksAllocated');
    }

    setNetTasksAllocated(netTasksAllocated: number): this {
        return this.clone(
            this._state.set('netTasksAllocated', netTasksAllocated)
        );
    }

    get netTasksUnallocated(): number {
        return this._state.get('netTasksUnallocated');
    }

    setNetTasksUnallocated(netTasksUnallocated: number): this {
        return this.clone(
            this._state.set('netTasksUnallocated', netTasksUnallocated)
        );
    }

    get netTasksMoved(): number {
        return this._state.get('netTasksMoved');
    }

    setNetTasksMoved(netTasksMoved: number): this {
        return this.clone(this._state.set('netTasksMoved', netTasksMoved));
    }

    get netToursAltered(): number {
        return this._state.get('netToursAltered');
    }

    setNetToursAltered(netToursAltered: number): this {
        return this.clone(this._state.set('netToursAltered', netToursAltered));
    }

    get vehiclesAdded(): number {
        return this._state.get('vehiclesAdded');
    }

    setVehiclesAdded(vehiclesAdded: number): this {
        return this.clone(this._state.set('vehiclesAdded', vehiclesAdded));
    }

    get vehiclesChanged(): number {
        return this._state.get('vehiclesChanged');
    }

    setVehiclesChanged(vehiclesChanged: number): this {
        return this.clone(this._state.set('vehiclesChanged', vehiclesChanged));
    }

    get vehiclesDeleted(): number {
        return this._state.get('vehiclesDeleted');
    }

    setVehiclesDeleted(vehiclesDeleted: number): this {
        return this.clone(this._state.set('vehiclesDeleted', vehiclesDeleted));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Resources extends ImmutableModel {
    static fromJS(json: ResourcesModelType): Resources {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): ResourcesModelType {
        return {};
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UnassignedTask extends ImmutableModel {
    static fromJS(json: UnassignedTaskModelType): UnassignedTask {
        const state: Object = Object.assign({}, json);
        state.visits = Immutable.List(state.visits).map(item =>
            TourVisit.fromJS(item)
        );
        state.skills = Skills.fromJS(state.skills);
        state.possibleDays = Immutable.List(state.possibleDays).map(item =>
            TourDay.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): UnassignedTaskModelType {
        return {
            clientId: this.clientId,
            taskId: this.taskId,
            userId: this.userId,
            visits: this.visits.toArray().map(item => item.toJS()),
            skills: this.skills.toJS(),
            possibleDays: this.possibleDays.toArray().map(item => item.toJS())
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get taskId(): string {
        return this._state.get('taskId');
    }

    setTaskId(taskId: string): this {
        return this.clone(this._state.set('taskId', taskId));
    }

    get userId(): ?string {
        return this._state.get('userId');
    }

    setUserId(userId: ?string): this {
        return this.clone(this._state.set('userId', userId));
    }

    get visits(): Immutable.List<TourVisit> {
        return this._state.get('visits');
    }

    setVisits(visits: Immutable.List<TourVisit>): this {
        return this.clone(this._state.set('visits', visits));
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    setSkills(skills: Skills): this {
        return this.clone(this._state.set('skills', skills));
    }

    get possibleDays(): Immutable.List<TourDay> {
        return this._state.get('possibleDays');
    }

    setPossibleDays(possibleDays: Immutable.List<TourDay>): this {
        return this.clone(this._state.set('possibleDays', possibleDays));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Vehicle extends ImmutableModel {
    static fromJS(json: VehicleModelType): Vehicle {
        const state: Object = Object.assign({}, json);
        state.id = VehicleId.fromJS(state.id);
        state.vehicleModel = VehicleModel.fromJS(state.vehicleModel);
        state.operationShift = OperationShift.fromJS(state.operationShift);
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): VehicleModelType {
        return {
            id: this.id.toJS(),
            vehicleModel: this.vehicleModel.toJS(),
            operationShift: this.operationShift.toJS(),
            resourceLimits: this.resourceLimits
                .toArray()
                .map(item => item.toJS())
        };
    }

    get id(): VehicleId {
        return this._state.get('id');
    }

    setId(id: VehicleId): this {
        return this.clone(this._state.set('id', id));
    }

    get vehicleModel(): VehicleModel {
        return this._state.get('vehicleModel');
    }

    setVehicleModel(vehicleModel: VehicleModel): this {
        return this.clone(this._state.set('vehicleModel', vehicleModel));
    }

    get operationShift(): OperationShift {
        return this._state.get('operationShift');
    }

    setOperationShift(operationShift: OperationShift): this {
        return this.clone(this._state.set('operationShift', operationShift));
    }

    get resourceLimits(): Immutable.List<ResourceLimit> {
        return this._state.get('resourceLimits');
    }

    setResourceLimits(resourceLimits: Immutable.List<ResourceLimit>): this {
        return this.clone(this._state.set('resourceLimits', resourceLimits));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TourVisit extends ImmutableModel {
    static fromJS(json: TourVisitModelType): TourVisit {
        const state: Object = Object.assign({}, json);
        state.chosenLocation = VisitLocation.fromJS(state.chosenLocation);
        state.predictedTravel = TimeDistancePair.fromJS(state.predictedTravel);
        state.volume = VisitVolume.fromJS(state.volume);
        state.resources = Immutable.List(state.resources).map(item =>
            VisitResource.fromJS(item)
        );
        state.violations = Immutable.List(state.violations).map(item =>
            ConstraintViolation.fromJS(item)
        );
        state.orderDetail = OrderDetail.fromJS(state.orderDetail);
        state.amountTransaction = AmountTransaction.fromJS(
            state.amountTransaction
        );
        state.appFields = AppFields.fromJS(state.appFields);
        state.skills = Skills.fromJS(state.skills);
        state.possibleDays = Immutable.List(state.possibleDays).map(item =>
            TourDay.fromJS(item)
        );
        state.locationId = state.locationId
            ? LocationId.fromJS(state.locationId)
            : state.locationId;
        state.locationDetails = state.locationDetails
            ? MasterLocation.fromJS(state.locationDetails)
            : state.locationDetails;
        state.statusUpdates = TaskStatusUpdate.fromJS(state.statusUpdates);
        state.triggeredGeofences = state.triggeredGeofences
            ? Immutable.List(state.triggeredGeofences).map(item =>
                  TriggeredGeofence.fromJS(item)
              )
            : state.triggeredGeofences;
        state.routes = Immutable.List(state.routes).map(item =>
            ZoneMapGeometry.fromJS(item)
        );
        state.team = Team.fromJS(state.team);
        return new this(Immutable.Map(state));
    }

    toJS(): TourVisitModelType {
        return {
            id: this.id,
            sourceId: this.sourceId,
            clientId: this.clientId,
            allocatedPlanId: this.allocatedPlanId,
            taskStatus: this.taskStatus,
            taskPriority: this.taskPriority,
            visitStatus: this.visitStatus,
            chosenLocation: this.chosenLocation.toJS(),
            currentEta: this.currentEta,
            currentEtd: this.currentEtd,
            initialEta: this.initialEta,
            initialEtd: this.initialEtd,
            initialEtt: this.initialEtt,
            idleDuration: this.idleDuration,
            currentEtt: this.currentEtt,
            slaBreached: this.slaBreached,
            predictedTravel: this.predictedTravel.toJS(),
            volume: this.volume.toJS(),
            volumeFilled: this.volumeFilled,
            resources: this.resources.toArray().map(item => item.toJS()),
            visitSource: this.visitSource,
            visitType: this.visitType,
            transactionType: this.transactionType,
            justModified: this.justModified,
            violations: this.violations.toArray().map(item => item.toJS()),
            orderDetail: this.orderDetail.toJS(),
            amountTransaction: this.amountTransaction.toJS(),
            sourceOrderId: this.sourceOrderId,
            userVisitType: this.userVisitType,
            breakType: this.breakType,
            appFields: this.appFields.toJS(),
            skills: this.skills.toJS(),
            possibleDays: this.possibleDays.toArray().map(item => item.toJS()),
            predictedPolyline: this.predictedPolyline,
            locationId: this.locationId
                ? this.locationId.toJS()
                : this.locationId,
            locationDetails: this.locationDetails
                ? this.locationDetails.toJS()
                : this.locationDetails,
            tardiness: this.tardiness,
            statusUpdates: this.statusUpdates.toJS(),
            triggeredGeofences: this.triggeredGeofences
                ? this.triggeredGeofences.toArray().map(item => item.toJS())
                : this.triggeredGeofences,
            routeId: this.routeId,
            routes: this.routes.toArray().map(item => item.toJS()),
            team: this.team.toJS(),
            returnTask: this.returnTask,
            category: this.category,
            compartmentId: this.compartmentId
        };
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get sourceId(): string {
        return this._state.get('sourceId');
    }

    setSourceId(sourceId: string): this {
        return this.clone(this._state.set('sourceId', sourceId));
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get allocatedPlanId(): string {
        return this._state.get('allocatedPlanId');
    }

    setAllocatedPlanId(allocatedPlanId: string): this {
        return this.clone(this._state.set('allocatedPlanId', allocatedPlanId));
    }

    get taskStatus(): TaskStatusEnum {
        return this._state.get('taskStatus');
    }

    setTaskStatus(taskStatus: TaskStatusEnum): this {
        return this.clone(this._state.set('taskStatus', taskStatus));
    }

    get taskPriority(): ?number {
        return this._state.get('taskPriority');
    }

    setTaskPriority(taskPriority: ?number): this {
        return this.clone(this._state.set('taskPriority', taskPriority));
    }

    get visitStatus(): VisitStatusEnum {
        return this._state.get('visitStatus');
    }

    setVisitStatus(visitStatus: VisitStatusEnum): this {
        return this.clone(this._state.set('visitStatus', visitStatus));
    }

    get chosenLocation(): VisitLocation {
        return this._state.get('chosenLocation');
    }

    setChosenLocation(chosenLocation: VisitLocation): this {
        return this.clone(this._state.set('chosenLocation', chosenLocation));
    }

    get currentEta(): string {
        return this._state.get('currentEta');
    }

    setCurrentEta(currentEta: string): this {
        return this.clone(this._state.set('currentEta', currentEta));
    }

    get currentEtd(): string {
        return this._state.get('currentEtd');
    }

    setCurrentEtd(currentEtd: string): this {
        return this.clone(this._state.set('currentEtd', currentEtd));
    }

    get initialEta(): string {
        return this._state.get('initialEta');
    }

    setInitialEta(initialEta: string): this {
        return this.clone(this._state.set('initialEta', initialEta));
    }

    get initialEtd(): string {
        return this._state.get('initialEtd');
    }

    setInitialEtd(initialEtd: string): this {
        return this.clone(this._state.set('initialEtd', initialEtd));
    }

    get initialEtt(): string {
        return this._state.get('initialEtt');
    }

    setInitialEtt(initialEtt: string): this {
        return this.clone(this._state.set('initialEtt', initialEtt));
    }

    get idleDuration(): number {
        return this._state.get('idleDuration');
    }

    setIdleDuration(idleDuration: number): this {
        return this.clone(this._state.set('idleDuration', idleDuration));
    }

    get currentEtt(): string {
        return this._state.get('currentEtt');
    }

    setCurrentEtt(currentEtt: string): this {
        return this.clone(this._state.set('currentEtt', currentEtt));
    }

    get slaBreached(): boolean {
        return this._state.get('slaBreached');
    }

    setSlaBreached(slaBreached: boolean): this {
        return this.clone(this._state.set('slaBreached', slaBreached));
    }

    get predictedTravel(): TimeDistancePair {
        return this._state.get('predictedTravel');
    }

    setPredictedTravel(predictedTravel: TimeDistancePair): this {
        return this.clone(this._state.set('predictedTravel', predictedTravel));
    }

    get volume(): VisitVolume {
        return this._state.get('volume');
    }

    setVolume(volume: VisitVolume): this {
        return this.clone(this._state.set('volume', volume));
    }

    get volumeFilled(): number {
        return this._state.get('volumeFilled');
    }

    setVolumeFilled(volumeFilled: number): this {
        return this.clone(this._state.set('volumeFilled', volumeFilled));
    }

    get resources(): Immutable.List<VisitResource> {
        return this._state.get('resources');
    }

    setResources(resources: Immutable.List<VisitResource>): this {
        return this.clone(this._state.set('resources', resources));
    }

    get visitSource(): VisitSourceEnum {
        return this._state.get('visitSource');
    }

    setVisitSource(visitSource: VisitSourceEnum): this {
        return this.clone(this._state.set('visitSource', visitSource));
    }

    get visitType(): VisitTypeEnum {
        return this._state.get('visitType');
    }

    setVisitType(visitType: VisitTypeEnum): this {
        return this.clone(this._state.set('visitType', visitType));
    }

    get transactionType(): TransactionTypeEnum {
        return this._state.get('transactionType');
    }

    setTransactionType(transactionType: TransactionTypeEnum): this {
        return this.clone(this._state.set('transactionType', transactionType));
    }

    get justModified(): boolean {
        return this._state.get('justModified');
    }

    setJustModified(justModified: boolean): this {
        return this.clone(this._state.set('justModified', justModified));
    }

    get violations(): Immutable.List<ConstraintViolation> {
        return this._state.get('violations');
    }

    setViolations(violations: Immutable.List<ConstraintViolation>): this {
        return this.clone(this._state.set('violations', violations));
    }

    get orderDetail(): OrderDetail {
        return this._state.get('orderDetail');
    }

    setOrderDetail(orderDetail: OrderDetail): this {
        return this.clone(this._state.set('orderDetail', orderDetail));
    }

    get amountTransaction(): AmountTransaction {
        return this._state.get('amountTransaction');
    }

    setAmountTransaction(amountTransaction: AmountTransaction): this {
        return this.clone(
            this._state.set('amountTransaction', amountTransaction)
        );
    }

    get sourceOrderId(): string {
        return this._state.get('sourceOrderId');
    }

    setSourceOrderId(sourceOrderId: string): this {
        return this.clone(this._state.set('sourceOrderId', sourceOrderId));
    }

    get userVisitType(): UserVisitTypeEnum {
        return this._state.get('userVisitType');
    }

    setUserVisitType(userVisitType: UserVisitTypeEnum): this {
        return this.clone(this._state.set('userVisitType', userVisitType));
    }

    get breakType(): GenericBreakTypeEnum {
        return this._state.get('breakType');
    }

    setBreakType(breakType: GenericBreakTypeEnum): this {
        return this.clone(this._state.set('breakType', breakType));
    }

    get appFields(): AppFields {
        return this._state.get('appFields');
    }

    setAppFields(appFields: AppFields): this {
        return this.clone(this._state.set('appFields', appFields));
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    setSkills(skills: Skills): this {
        return this.clone(this._state.set('skills', skills));
    }

    get possibleDays(): Immutable.List<TourDay> {
        return this._state.get('possibleDays');
    }

    setPossibleDays(possibleDays: Immutable.List<TourDay>): this {
        return this.clone(this._state.set('possibleDays', possibleDays));
    }

    get predictedPolyline(): ?string {
        return this._state.get('predictedPolyline');
    }

    setPredictedPolyline(predictedPolyline: ?string): this {
        return this.clone(
            this._state.set('predictedPolyline', predictedPolyline)
        );
    }

    get locationId(): ?LocationId {
        return this._state.get('locationId');
    }

    setLocationId(locationId: ?LocationId): this {
        return this.clone(this._state.set('locationId', locationId));
    }

    get locationDetails(): ?MasterLocation {
        return this._state.get('locationDetails');
    }

    setLocationDetails(locationDetails: ?MasterLocation): this {
        return this.clone(this._state.set('locationDetails', locationDetails));
    }

    get tardiness(): ?number {
        return this._state.get('tardiness');
    }

    setTardiness(tardiness: ?number): this {
        return this.clone(this._state.set('tardiness', tardiness));
    }

    get statusUpdates(): TaskStatusUpdate {
        return this._state.get('statusUpdates');
    }

    setStatusUpdates(statusUpdates: TaskStatusUpdate): this {
        return this.clone(this._state.set('statusUpdates', statusUpdates));
    }

    get triggeredGeofences(): ?Immutable.List<TriggeredGeofence> {
        return this._state.get('triggeredGeofences');
    }

    setTriggeredGeofences(
        triggeredGeofences: ?Immutable.List<TriggeredGeofence>
    ): this {
        return this.clone(
            this._state.set('triggeredGeofences', triggeredGeofences)
        );
    }

    get routeId(): string {
        return this._state.get('routeId');
    }

    setRouteId(routeId: string): this {
        return this.clone(this._state.set('routeId', routeId));
    }

    get routes(): Immutable.List<ZoneMapGeometry> {
        return this._state.get('routes');
    }

    setRoutes(routes: Immutable.List<ZoneMapGeometry>): this {
        return this.clone(this._state.set('routes', routes));
    }

    get team(): Team {
        return this._state.get('team');
    }

    setTeam(team: Team): this {
        return this.clone(this._state.set('team', team));
    }

    get returnTask(): ?boolean {
        return this._state.get('returnTask');
    }

    setReturnTask(returnTask: ?boolean): this {
        return this.clone(this._state.set('returnTask', returnTask));
    }

    get category(): ?string {
        return this._state.get('category');
    }

    setCategory(category: ?string): this {
        return this.clone(this._state.set('category', category));
    }

    get compartmentId(): ?string {
        return this._state.get('compartmentId');
    }

    setCompartmentId(compartmentId: ?string): this {
        return this.clone(this._state.set('compartmentId', compartmentId));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UserTourSummary extends ImmutableModel {
    static fromJS(json: UserTourSummaryModelType): UserTourSummary {
        const state: Object = Object.assign({}, json);
        state.customMetrics = Immutable.Map(state.customMetrics);
        state.volume = ResourceUtilization.fromJS(state.volume);
        state.resources = state.resources
            ? Resources.fromJS(state.resources)
            : state.resources;
        state.vehiclesOperationSlot = TimeSlot.fromJS(
            state.vehiclesOperationSlot
        );
        state.usedSkills = Skills.fromJS(state.usedSkills);
        state.freightCost = state.freightCost
            ? Amount.fromJS(state.freightCost)
            : state.freightCost;
        state.usedRoutes = state.usedRoutes
            ? Immutable.List(state.usedRoutes).map(item =>
                  UsedRoutes.fromJS(item)
              )
            : state.usedRoutes;
        state.customFieldMetrics = Immutable.List(
            state.customFieldMetrics
        ).map(item => CustomFieldMetric.fromJS(item));
        return new this(Immutable.Map(state));
    }

    toJS(): UserTourSummaryModelType {
        const js: UserTourSummaryModelType = {
            clientId: this.clientId,
            startTime: this.startTime,
            customMetrics: this.customMetrics.toObject(),
            endTime: this.endTime,
            taskCount: this.taskCount,
            visitCount: this.visitCount,
            totalTravelDistance: this.totalTravelDistance,
            totalTravelDuration: this.totalTravelDuration,
            totalTardiness: this.totalTardiness,
            totalIdleDuration: this.totalIdleDuration,
            totalTransactionDuration: this.totalTransactionDuration,
            volume: this.volume.toJS(),
            resources: this.resources ? this.resources.toJS() : this.resources,
            vehiclesOperationSlot: this.vehiclesOperationSlot.toJS(),
            usedSkills: this.usedSkills.toJS(),
            freightCost: this.freightCost
                ? this.freightCost.toJS()
                : this.freightCost,
            usedRoutes: this.usedRoutes
                ? this.usedRoutes.toArray().map(item => item.toJS())
                : this.usedRoutes,
            customFieldMetrics: this.customFieldMetrics
                .toArray()
                .map(item => item.toJS()),
            tourCarbonEmission: this.tourCarbonEmission
        };

        if (this.totalDuration != null) {
            js.totalDuration = this.totalDuration;
        }

        return js;
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get startTime(): string {
        return this._state.get('startTime');
    }

    setStartTime(startTime: string): this {
        return this.clone(this._state.set('startTime', startTime));
    }

    get customMetrics(): Immutable.Map<string, string> {
        return this._state.get('customMetrics');
    }

    setCustomMetrics(customMetrics: Immutable.Map<string, string>): this {
        return this.clone(this._state.set('customMetrics', customMetrics));
    }

    get endTime(): string {
        return this._state.get('endTime');
    }

    setEndTime(endTime: string): this {
        return this.clone(this._state.set('endTime', endTime));
    }

    get taskCount(): number {
        return this._state.get('taskCount');
    }

    setTaskCount(taskCount: number): this {
        return this.clone(this._state.set('taskCount', taskCount));
    }

    get visitCount(): number {
        return this._state.get('visitCount');
    }

    setVisitCount(visitCount: number): this {
        return this.clone(this._state.set('visitCount', visitCount));
    }

    get totalTravelDistance(): number {
        return this._state.get('totalTravelDistance');
    }

    setTotalTravelDistance(totalTravelDistance: number): this {
        return this.clone(
            this._state.set('totalTravelDistance', totalTravelDistance)
        );
    }

    get totalTravelDuration(): number {
        return this._state.get('totalTravelDuration');
    }

    setTotalTravelDuration(totalTravelDuration: number): this {
        return this.clone(
            this._state.set('totalTravelDuration', totalTravelDuration)
        );
    }

    get totalTardiness(): number {
        return this._state.get('totalTardiness');
    }

    setTotalTardiness(totalTardiness: number): this {
        return this.clone(this._state.set('totalTardiness', totalTardiness));
    }

    get totalIdleDuration(): number {
        return this._state.get('totalIdleDuration');
    }

    setTotalIdleDuration(totalIdleDuration: number): this {
        return this.clone(
            this._state.set('totalIdleDuration', totalIdleDuration)
        );
    }

    get totalTransactionDuration(): number {
        return this._state.get('totalTransactionDuration');
    }

    setTotalTransactionDuration(totalTransactionDuration: number): this {
        return this.clone(
            this._state.set(
                'totalTransactionDuration',
                totalTransactionDuration
            )
        );
    }

    get totalDuration(): ?number {
        return this._state.get('totalDuration');
    }

    setTotalDuration(totalDuration: ?number): this {
        return this.clone(this._state.set('totalDuration', totalDuration));
    }

    get volume(): ResourceUtilization {
        return this._state.get('volume');
    }

    setVolume(volume: ResourceUtilization): this {
        return this.clone(this._state.set('volume', volume));
    }

    get resources(): ?Resources {
        return this._state.get('resources');
    }

    setResources(resources: ?Resources): this {
        return this.clone(this._state.set('resources', resources));
    }

    get vehiclesOperationSlot(): TimeSlot {
        return this._state.get('vehiclesOperationSlot');
    }

    setVehiclesOperationSlot(vehiclesOperationSlot: TimeSlot): this {
        return this.clone(
            this._state.set('vehiclesOperationSlot', vehiclesOperationSlot)
        );
    }

    get usedSkills(): Skills {
        return this._state.get('usedSkills');
    }

    setUsedSkills(usedSkills: Skills): this {
        return this.clone(this._state.set('usedSkills', usedSkills));
    }

    get freightCost(): ?Amount {
        return this._state.get('freightCost');
    }

    setFreightCost(freightCost: ?Amount): this {
        return this.clone(this._state.set('freightCost', freightCost));
    }

    get usedRoutes(): ?Immutable.List<UsedRoutes> {
        return this._state.get('usedRoutes');
    }

    setUsedRoutes(usedRoutes: ?Immutable.List<UsedRoutes>): this {
        return this.clone(this._state.set('usedRoutes', usedRoutes));
    }

    get customFieldMetrics(): Immutable.List<CustomFieldMetric> {
        return this._state.get('customFieldMetrics');
    }

    setCustomFieldMetrics(
        customFieldMetrics: Immutable.List<CustomFieldMetric>
    ): this {
        return this.clone(
            this._state.set('customFieldMetrics', customFieldMetrics)
        );
    }

    get tourCarbonEmission(): ?number {
        return this._state.get('tourCarbonEmission');
    }

    setTourCarbonEmission(tourCarbonEmission: ?number): this {
        return this.clone(
            this._state.set('tourCarbonEmission', tourCarbonEmission)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TourId extends ImmutableModel {
    static fromJS(json: TourIdModelType): TourId {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): TourIdModelType {
        return {
            clientId: this.clientId,
            tourId: this.tourId
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get tourId(): string {
        return this._state.get('tourId');
    }

    setTourId(tourId: string): this {
        return this.clone(this._state.set('tourId', tourId));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UserVehicle extends ImmutableModel {
    static fromJS(json: UserVehicleModelType): UserVehicle {
        const state: Object = Object.assign({}, json);
        state.id = VehicleId.fromJS(state.id);
        state.vehicleModel = VehicleModel.fromJS(state.vehicleModel);
        state.operationShift = OperationShift.fromJS(state.operationShift);
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        state.userAlias = state.userAlias
            ? UserAlias.fromJS(state.userAlias)
            : state.userAlias;
        state.skills = Skills.fromJS(state.skills);
        state.vehicleCostModel = state.vehicleCostModel
            ? VehicleCostModel.fromJS(state.vehicleCostModel)
            : state.vehicleCostModel;
        state.crossDockVehicleConfig = state.crossDockVehicleConfig
            ? CrossDockVehicleConfig.fromJS(state.crossDockVehicleConfig)
            : state.crossDockVehicleConfig;
        return new this(Immutable.Map(state));
    }

    toJS(): UserVehicleModelType {
        return {
            idAttr: this.idAttr,
            id: this.id.toJS(),
            vehicleModel: this.vehicleModel.toJS(),
            operationShift: this.operationShift.toJS(),
            resourceLimits: this.resourceLimits
                .toArray()
                .map(item => item.toJS()),
            userAlias: this.userAlias ? this.userAlias.toJS() : this.userAlias,
            skills: this.skills.toJS(),
            rating: this.rating,
            vehicleCostModel: this.vehicleCostModel
                ? this.vehicleCostModel.toJS()
                : this.vehicleCostModel,
            isCrossDockEnabled: this.isCrossDockEnabled,
            crossDockVehicleConfig: this.crossDockVehicleConfig
                ? this.crossDockVehicleConfig.toJS()
                : this.crossDockVehicleConfig
        };
    }

    get idAttr(): string {
        return this._state.get('idAttr');
    }

    setIdAttr(idAttr: string): this {
        return this.clone(this._state.set('idAttr', idAttr));
    }

    get id(): VehicleId {
        return this._state.get('id');
    }

    setId(id: VehicleId): this {
        return this.clone(this._state.set('id', id));
    }

    get vehicleModel(): VehicleModel {
        return this._state.get('vehicleModel');
    }

    setVehicleModel(vehicleModel: VehicleModel): this {
        return this.clone(this._state.set('vehicleModel', vehicleModel));
    }

    get operationShift(): OperationShift {
        return this._state.get('operationShift');
    }

    setOperationShift(operationShift: OperationShift): this {
        return this.clone(this._state.set('operationShift', operationShift));
    }

    get resourceLimits(): Immutable.List<ResourceLimit> {
        return this._state.get('resourceLimits');
    }

    setResourceLimits(resourceLimits: Immutable.List<ResourceLimit>): this {
        return this.clone(this._state.set('resourceLimits', resourceLimits));
    }

    get userAlias(): ?UserAlias {
        return this._state.get('userAlias');
    }

    setUserAlias(userAlias: ?UserAlias): this {
        return this.clone(this._state.set('userAlias', userAlias));
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    setSkills(skills: Skills): this {
        return this.clone(this._state.set('skills', skills));
    }

    get rating(): number {
        return this._state.get('rating');
    }

    setRating(rating: number): this {
        return this.clone(this._state.set('rating', rating));
    }

    get vehicleCostModel(): ?VehicleCostModel {
        return this._state.get('vehicleCostModel');
    }

    setVehicleCostModel(vehicleCostModel: ?VehicleCostModel): this {
        return this.clone(
            this._state.set('vehicleCostModel', vehicleCostModel)
        );
    }

    get isCrossDockEnabled(): ?boolean {
        return this._state.get('isCrossDockEnabled');
    }

    setIsCrossDockEnabled(isCrossDockEnabled: ?boolean): this {
        return this.clone(
            this._state.set('isCrossDockEnabled', isCrossDockEnabled)
        );
    }

    get crossDockVehicleConfig(): ?CrossDockVehicleConfig {
        return this._state.get('crossDockVehicleConfig');
    }

    setCrossDockVehicleConfig(
        crossDockVehicleConfig: ?CrossDockVehicleConfig
    ): this {
        return this.clone(
            this._state.set('crossDockVehicleConfig', crossDockVehicleConfig)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AssignedUser extends ImmutableModel {
    static fromJS(json: AssignedUserModelType): AssignedUser {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): AssignedUserModelType {
        return {
            carrierClientId: this.carrierClientId,
            userId: this.userId,
            name: this.name
        };
    }

    get carrierClientId(): string {
        return this._state.get('carrierClientId');
    }

    setCarrierClientId(carrierClientId: string): this {
        return this.clone(this._state.set('carrierClientId', carrierClientId));
    }

    get userId(): string {
        return this._state.get('userId');
    }

    setUserId(userId: string): this {
        return this.clone(this._state.set('userId', userId));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class ResourceUtilization extends ImmutableModel {
    static fromJS(json: ResourceUtilizationModelType): ResourceUtilization {
        const state: Object = Object.assign({}, json);
        state.definition = state.definition
            ? ResourceDefinition.fromJS(state.definition)
            : state.definition;
        return new this(Immutable.Map(state));
    }

    toJS(): ResourceUtilizationModelType {
        return {
            used: this.used,
            total: this.total,
            available: this.available,
            required: this.required,
            definition: this.definition
                ? this.definition.toJS()
                : this.definition
        };
    }

    get used(): number {
        return this._state.get('used');
    }

    setUsed(used: number): this {
        return this.clone(this._state.set('used', used));
    }

    get total(): number {
        return this._state.get('total');
    }

    setTotal(total: number): this {
        return this.clone(this._state.set('total', total));
    }

    get available(): number {
        return this._state.get('available');
    }

    setAvailable(available: number): this {
        return this.clone(this._state.set('available', available));
    }

    get required(): number {
        return this._state.get('required');
    }

    setRequired(required: number): this {
        return this.clone(this._state.set('required', required));
    }

    get definition(): ?ResourceDefinition {
        return this._state.get('definition');
    }

    setDefinition(definition: ?ResourceDefinition): this {
        return this.clone(this._state.set('definition', definition));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VehicleId extends ImmutableModel {
    static fromJS(json: VehicleIdModelType): VehicleId {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): VehicleIdModelType {
        const js: VehicleIdModelType = {
            clientId: this.clientId,
            id: this.id
        };

        if (this.version != null) {
            js.version = this.version;
        }

        return js;
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get version(): ?number {
        return this._state.get('version');
    }

    setVersion(version: ?number): this {
        return this.clone(this._state.set('version', version));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VehicleModel extends ImmutableModel {
    static fromJS(json: VehicleModelModelType): VehicleModel {
        const state: Object = Object.assign({}, json);
        state.volumeLimit = VolumeLimit.fromJS(state.volumeLimit);
        state.id = VehicleModelId.fromJS(state.id);
        state.costModel = state.costModel
            ? VehicleCostModel.fromJS(state.costModel)
            : state.costModel;
        state.skills = state.skills
            ? Skills.fromJS(state.skills)
            : state.skills;
        state.resourceLimits = Immutable.List(state.resourceLimits).map(item =>
            ResourceLimit.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): VehicleModelModelType {
        return {
            name: this.name,
            idAttr: this.idAttr,
            volumeLimit: this.volumeLimit.toJS(),
            id: this.id.toJS(),
            costModel: this.costModel ? this.costModel.toJS() : this.costModel,
            skills: this.skills ? this.skills.toJS() : this.skills,
            resourceLimits: this.resourceLimits
                .toArray()
                .map(item => item.toJS())
        };
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get idAttr(): string {
        return this._state.get('idAttr');
    }

    setIdAttr(idAttr: string): this {
        return this.clone(this._state.set('idAttr', idAttr));
    }

    get volumeLimit(): VolumeLimit {
        return this._state.get('volumeLimit');
    }

    setVolumeLimit(volumeLimit: VolumeLimit): this {
        return this.clone(this._state.set('volumeLimit', volumeLimit));
    }

    get id(): VehicleModelId {
        return this._state.get('id');
    }

    setId(id: VehicleModelId): this {
        return this.clone(this._state.set('id', id));
    }

    get costModel(): ?VehicleCostModel {
        return this._state.get('costModel');
    }

    setCostModel(costModel: ?VehicleCostModel): this {
        return this.clone(this._state.set('costModel', costModel));
    }

    get skills(): ?Skills {
        return this._state.get('skills');
    }

    setSkills(skills: ?Skills): this {
        return this.clone(this._state.set('skills', skills));
    }

    get resourceLimits(): Immutable.List<ResourceLimit> {
        return this._state.get('resourceLimits');
    }

    setResourceLimits(resourceLimits: Immutable.List<ResourceLimit>): this {
        return this.clone(this._state.set('resourceLimits', resourceLimits));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class OperationShift extends ImmutableModel {
    static fromJS(json: OperationShiftModelType): OperationShift {
        const state: Object = Object.assign({}, json);
        state.startShiftInstant = ShiftInstant.fromJS(state.startShiftInstant);
        state.startLocation = state.startLocation
            ? MinimalLocation.fromJS(state.startLocation)
            : state.startLocation;
        state.endShiftInstant = ShiftInstant.fromJS(state.endShiftInstant);
        state.endLocation = state.endLocation
            ? MinimalLocation.fromJS(state.endLocation)
            : state.endLocation;
        state.breaks = Immutable.List(state.breaks).map(item =>
            Break.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): OperationShiftModelType {
        return {
            startTime: this.startTime,
            startShiftInstant: this.startShiftInstant.toJS(),
            startLocation: this.startLocation
                ? this.startLocation.toJS()
                : this.startLocation,
            endTime: this.endTime,
            endShiftInstant: this.endShiftInstant.toJS(),
            endLocation: this.endLocation
                ? this.endLocation.toJS()
                : this.endLocation,
            breaks: this.breaks.toArray().map(item => item.toJS())
        };
    }

    get startTime(): string {
        return this._state.get('startTime');
    }

    setStartTime(startTime: string): this {
        return this.clone(this._state.set('startTime', startTime));
    }

    get startShiftInstant(): ShiftInstant {
        return this._state.get('startShiftInstant');
    }

    setStartShiftInstant(startShiftInstant: ShiftInstant): this {
        return this.clone(
            this._state.set('startShiftInstant', startShiftInstant)
        );
    }

    get startLocation(): ?MinimalLocation {
        return this._state.get('startLocation');
    }

    setStartLocation(startLocation: ?MinimalLocation): this {
        return this.clone(this._state.set('startLocation', startLocation));
    }

    get endTime(): string {
        return this._state.get('endTime');
    }

    setEndTime(endTime: string): this {
        return this.clone(this._state.set('endTime', endTime));
    }

    get endShiftInstant(): ShiftInstant {
        return this._state.get('endShiftInstant');
    }

    setEndShiftInstant(endShiftInstant: ShiftInstant): this {
        return this.clone(this._state.set('endShiftInstant', endShiftInstant));
    }

    get endLocation(): ?MinimalLocation {
        return this._state.get('endLocation');
    }

    setEndLocation(endLocation: ?MinimalLocation): this {
        return this.clone(this._state.set('endLocation', endLocation));
    }

    get breaks(): Immutable.List<Break> {
        return this._state.get('breaks');
    }

    setBreaks(breaks: Immutable.List<Break>): this {
        return this.clone(this._state.set('breaks', breaks));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VisitLocation extends ImmutableModel {
    static fromJS(json: VisitLocationModelType): VisitLocation {
        const state: Object = Object.assign({}, json);
        state.slot = VisitSlot.fromJS(state.slot);
        state.contact = state.contact
            ? ContactPoint.fromJS(state.contact)
            : state.contact;
        state.address = StructuredAddress.fromJS(state.address);
        state.geometry = Geometry.fromJS(state.geometry);
        state.geocodingMetadata = GeocodingMetadata.fromJS(
            state.geocodingMetadata
        );
        state.nonAvailableSlots = Immutable.List(
            state.nonAvailableSlots
        ).map(item => VisitSlot.fromJS(item));
        return new this(Immutable.Map(state));
    }

    toJS(): VisitLocationModelType {
        return {
            id: this.id,
            slot: this.slot.toJS(),
            contact: this.contact ? this.contact.toJS() : this.contact,
            address: this.address.toJS(),
            geometry: this.geometry.toJS(),
            geocodingMetadata: this.geocodingMetadata.toJS(),
            nonAvailableSlots: this.nonAvailableSlots
                .toArray()
                .map(item => item.toJS())
        };
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get slot(): VisitSlot {
        return this._state.get('slot');
    }

    setSlot(slot: VisitSlot): this {
        return this.clone(this._state.set('slot', slot));
    }

    get contact(): ?ContactPoint {
        return this._state.get('contact');
    }

    setContact(contact: ?ContactPoint): this {
        return this.clone(this._state.set('contact', contact));
    }

    get address(): StructuredAddress {
        return this._state.get('address');
    }

    setAddress(address: StructuredAddress): this {
        return this.clone(this._state.set('address', address));
    }

    get geometry(): Geometry {
        return this._state.get('geometry');
    }

    setGeometry(geometry: Geometry): this {
        return this.clone(this._state.set('geometry', geometry));
    }

    get geocodingMetadata(): GeocodingMetadata {
        return this._state.get('geocodingMetadata');
    }

    setGeocodingMetadata(geocodingMetadata: GeocodingMetadata): this {
        return this.clone(
            this._state.set('geocodingMetadata', geocodingMetadata)
        );
    }

    get nonAvailableSlots(): Immutable.List<VisitSlot> {
        return this._state.get('nonAvailableSlots');
    }

    setNonAvailableSlots(nonAvailableSlots: Immutable.List<VisitSlot>): this {
        return this.clone(
            this._state.set('nonAvailableSlots', nonAvailableSlots)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TimeDistancePair extends ImmutableModel {
    static fromJS(json: TimeDistancePairModelType): TimeDistancePair {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): TimeDistancePairModelType {
        return {
            distance: this.distance,
            duration: this.duration
        };
    }

    get distance(): number {
        return this._state.get('distance');
    }

    setDistance(distance: number): this {
        return this.clone(this._state.set('distance', distance));
    }

    get duration(): number {
        return this._state.get('duration');
    }

    setDuration(duration: number): this {
        return this.clone(this._state.set('duration', duration));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VisitVolume extends ImmutableModel {
    static fromJS(json: VisitVolumeModelType): VisitVolume {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): VisitVolumeModelType {
        return {
            value: this.value,
            exchangeType: this.exchangeType
        };
    }

    get value(): string {
        return this._state.get('value');
    }

    setValue(value: string): this {
        return this.clone(this._state.set('value', value));
    }

    get exchangeType(): string {
        return this._state.get('exchangeType');
    }

    setExchangeType(exchangeType: string): this {
        return this.clone(this._state.set('exchangeType', exchangeType));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class ConstraintViolation extends ImmutableModel {
    static fromJS(json: ConstraintViolationModelType): ConstraintViolation {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): ConstraintViolationModelType {
        return {
            type: this.type,
            breachValue: this.breachValue,
            threshold: this.threshold,
            unit: this.unit,
            severity: this.severity,
            description: this.description,
            name: this.name
        };
    }

    get type(): ConstraintViolationTypeEnum {
        return this._state.get('type');
    }

    setType(type: ConstraintViolationTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }

    get breachValue(): string {
        return this._state.get('breachValue');
    }

    setBreachValue(breachValue: string): this {
        return this.clone(this._state.set('breachValue', breachValue));
    }

    get threshold(): string {
        return this._state.get('threshold');
    }

    setThreshold(threshold: string): this {
        return this.clone(this._state.set('threshold', threshold));
    }

    get unit(): string {
        return this._state.get('unit');
    }

    setUnit(unit: string): this {
        return this.clone(this._state.set('unit', unit));
    }

    get severity(): ConstraintViolationSeverityEnum {
        return this._state.get('severity');
    }

    setSeverity(severity: ConstraintViolationSeverityEnum): this {
        return this.clone(this._state.set('severity', severity));
    }

    get description(): string {
        return this._state.get('description');
    }

    setDescription(description: string): this {
        return this.clone(this._state.set('description', description));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class OrderDetail extends ImmutableModel {
    static fromJS(json: OrderDetailModelType): OrderDetail {
        const state: Object = Object.assign({}, json);
        state.lineItems = state.lineItems
            ? Immutable.List(state.lineItems).map(item => LineItem.fromJS(item))
            : state.lineItems;
        return new this(Immutable.Map(state));
    }

    toJS(): OrderDetailModelType {
        return {
            lineItems: this.lineItems
                ? this.lineItems.toArray().map(item => item.toJS())
                : this.lineItems
        };
    }

    get lineItems(): ?Immutable.List<LineItem> {
        return this._state.get('lineItems');
    }

    setLineItems(lineItems: ?Immutable.List<LineItem>): this {
        return this.clone(this._state.set('lineItems', lineItems));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UserAlias extends ImmutableModel {
    static fromJS(json: UserAliasModelType): UserAlias {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): UserAliasModelType {
        return {
            alias: this.alias,
            clientId: this.clientId,
            source: this.source
        };
    }

    get alias(): string {
        return this._state.get('alias');
    }

    setAlias(alias: string): this {
        return this.clone(this._state.set('alias', alias));
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get source(): UserSourceEnum {
        return this._state.get('source');
    }

    setSource(source: UserSourceEnum): this {
        return this.clone(this._state.set('source', source));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VehicleModelId extends ImmutableModel {
    static fromJS(json: VehicleModelIdModelType): VehicleModelId {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): VehicleModelIdModelType {
        return {
            clientId: this.clientId,
            modelId: this.modelId
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get modelId(): string {
        return this._state.get('modelId');
    }

    setModelId(modelId: string): this {
        return this.clone(this._state.set('modelId', modelId));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MinimalLocation extends ImmutableModel {
    static fromJS(json: MinimalLocationModelType): MinimalLocation {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): MinimalLocationModelType {
        return {
            lat: this.lat,
            lng: this.lng,
            description: this.description
        };
    }

    get lat(): number {
        return this._state.get('lat');
    }

    setLat(lat: number): this {
        return this.clone(this._state.set('lat', lat));
    }

    get lng(): number {
        return this._state.get('lng');
    }

    setLng(lng: number): this {
        return this.clone(this._state.set('lng', lng));
    }

    get description(): string {
        return this._state.get('description');
    }

    setDescription(description: string): this {
        return this.clone(this._state.set('description', description));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Break extends ImmutableModel {
    static fromJS(json: BreakModelType): Break {
        const state: Object = Object.assign({}, json);
        state.startShiftInstant = ShiftInstant.fromJS(state.startShiftInstant);
        state.startLocation = state.startLocation
            ? MinimalLocation.fromJS(state.startLocation)
            : state.startLocation;
        state.endShiftInstant = ShiftInstant.fromJS(state.endShiftInstant);
        state.endLocation = state.endLocation
            ? MinimalLocation.fromJS(state.endLocation)
            : state.endLocation;
        return new this(Immutable.Map(state));
    }

    toJS(): BreakModelType {
        return {
            startTime: this.startTime,
            startShiftInstant: this.startShiftInstant.toJS(),
            startLocation: this.startLocation
                ? this.startLocation.toJS()
                : this.startLocation,
            endTime: this.endTime,
            endShiftInstant: this.endShiftInstant.toJS(),
            endLocation: this.endLocation
                ? this.endLocation.toJS()
                : this.endLocation,
            transactionDuration: this.transactionDuration,
            breakType: this.breakType
        };
    }

    get startTime(): string {
        return this._state.get('startTime');
    }

    setStartTime(startTime: string): this {
        return this.clone(this._state.set('startTime', startTime));
    }

    get startShiftInstant(): ShiftInstant {
        return this._state.get('startShiftInstant');
    }

    setStartShiftInstant(startShiftInstant: ShiftInstant): this {
        return this.clone(
            this._state.set('startShiftInstant', startShiftInstant)
        );
    }

    get startLocation(): ?MinimalLocation {
        return this._state.get('startLocation');
    }

    setStartLocation(startLocation: ?MinimalLocation): this {
        return this.clone(this._state.set('startLocation', startLocation));
    }

    get endTime(): string {
        return this._state.get('endTime');
    }

    setEndTime(endTime: string): this {
        return this.clone(this._state.set('endTime', endTime));
    }

    get endShiftInstant(): ShiftInstant {
        return this._state.get('endShiftInstant');
    }

    setEndShiftInstant(endShiftInstant: ShiftInstant): this {
        return this.clone(this._state.set('endShiftInstant', endShiftInstant));
    }

    get endLocation(): ?MinimalLocation {
        return this._state.get('endLocation');
    }

    setEndLocation(endLocation: ?MinimalLocation): this {
        return this.clone(this._state.set('endLocation', endLocation));
    }

    get transactionDuration(): number {
        return this._state.get('transactionDuration');
    }

    setTransactionDuration(transactionDuration: number): this {
        return this.clone(
            this._state.set('transactionDuration', transactionDuration)
        );
    }

    get breakType(): BreakTypeEnum {
        return this._state.get('breakType');
    }

    setBreakType(breakType: BreakTypeEnum): this {
        return this.clone(this._state.set('breakType', breakType));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VisitSlot extends ImmutableModel {
    static fromJS(json: VisitSlotModelType): VisitSlot {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): VisitSlotModelType {
        return {
            startTime: this.startTime,
            endTime: this.endTime,
            transactionDuration: this.transactionDuration
        };
    }

    get startTime(): string {
        return this._state.get('startTime');
    }

    setStartTime(startTime: string): this {
        return this.clone(this._state.set('startTime', startTime));
    }

    get endTime(): string {
        return this._state.get('endTime');
    }

    setEndTime(endTime: string): this {
        return this.clone(this._state.set('endTime', endTime));
    }

    get transactionDuration(): number {
        return this._state.get('transactionDuration');
    }

    setTransactionDuration(transactionDuration: number): this {
        return this.clone(
            this._state.set('transactionDuration', transactionDuration)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Metric extends ImmutableModel {
    static fromJS(json: MetricModelType): Metric {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): MetricModelType {
        return {
            aggregation: this.aggregation,
            value: this.value
        };
    }

    get aggregation(): string {
        return this._state.get('aggregation');
    }

    setAggregation(aggregation: string): this {
        return this.clone(this._state.set('aggregation', aggregation));
    }

    get value(): number {
        return this._state.get('value');
    }

    setValue(value: number): this {
        return this.clone(this._state.set('value', value));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class CustomFieldMetric extends ImmutableModel {
    static fromJS(json: CustomFieldMetricModelType): CustomFieldMetric {
        const state: Object = Object.assign({}, json);
        state.metric = Metric.fromJS(state.metric);
        return new this(Immutable.Map(state));
    }

    toJS(): CustomFieldMetricModelType {
        return {
            key: this.key,
            metric: this.metric.toJS()
        };
    }

    get key(): string {
        return this._state.get('key');
    }

    setKey(key: string): this {
        return this.clone(this._state.set('key', key));
    }

    get metric(): Metric {
        return this._state.get('metric');
    }

    setMetric(metric: Metric): this {
        return this.clone(this._state.set('metric', metric));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class AdditionalValue extends ImmutableModel {
    static fromJS(json: AdditionalValueModelType): AdditionalValue {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): AdditionalValueModelType {
        return {
            url: this.url
        };
    }

    get url(): string {
        return this._state.get('url');
    }

    setUrl(url: string): this {
        return this.clone(this._state.set('url', url));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UsedRoutes extends ImmutableModel {
    static fromJS(json: UsedRoutesModelType): UsedRoutes {
        const state: Object = Object.assign({}, json);
        state.mapId = VersionedMapId.fromJS(state.mapId);
        return new this(Immutable.Map(state));
    }

    toJS(): UsedRoutesModelType {
        return {
            mapId: this.mapId.toJS(),
            routeId: this.routeId
        };
    }

    get mapId(): VersionedMapId {
        return this._state.get('mapId');
    }

    setMapId(mapId: VersionedMapId): this {
        return this.clone(this._state.set('mapId', mapId));
    }

    get routeId(): string {
        return this._state.get('routeId');
    }

    setRouteId(routeId: string): this {
        return this.clone(this._state.set('routeId', routeId));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VehicleCount extends ImmutableModel {
    static fromJS(json: VehicleCountModelType): VehicleCount {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): VehicleCountModelType {
        return {
            name: this.name,
            count: this.count
        };
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get count(): number {
        return this._state.get('count');
    }

    setCount(count: number): this {
        return this.clone(this._state.set('count', count));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class CarbonEmission extends ImmutableModel {
    static fromJS(json: CarbonEmissionModelType): CarbonEmission {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): CarbonEmissionModelType {
        return {
            equivalentTreeOffset: this.equivalentTreeOffset,
            savedCarbonEmission: this.savedCarbonEmission,
            plannedCarbonEmission: this.plannedCarbonEmission
        };
    }

    get equivalentTreeOffset(): number {
        return this._state.get('equivalentTreeOffset');
    }

    setEquivalentTreeOffset(equivalentTreeOffset: number): this {
        return this.clone(
            this._state.set('equivalentTreeOffset', equivalentTreeOffset)
        );
    }

    get savedCarbonEmission(): number {
        return this._state.get('savedCarbonEmission');
    }

    setSavedCarbonEmission(savedCarbonEmission: number): this {
        return this.clone(
            this._state.set('savedCarbonEmission', savedCarbonEmission)
        );
    }

    get plannedCarbonEmission(): number {
        return this._state.get('plannedCarbonEmission');
    }

    setPlannedCarbonEmission(plannedCarbonEmission: number): this {
        return this.clone(
            this._state.set('plannedCarbonEmission', plannedCarbonEmission)
        );
    }
}
